import React, { useState, useEffect, useRef } from 'react';
import { RotateCw, FileText, X, Trash2 } from 'lucide-react';
import { API_BASE } from '../apiConfig';

const styles = {
  container: {
    padding: '20px 0',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    marginBottom: '24px',
    position: 'relative',
  },
  stats: {
    display: 'inline-flex',
    alignItems: 'center',
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    overflow: 'hidden',
    height: '36px',
    paddingRight: '16px',
    boxShadow: '0 4px 10px rgba(0,0,0,0.04)',
  },
  statsIcon: {
    background: '#ef4444',
    width: '44px',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    position: 'relative',
    marginRight: '12px',
    clipPath: 'polygon(0% 0%, 85% 0%, 100% 50%, 85% 100%, 0% 100%)',
  },
  statsText: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '14px',
    color: 'var(--text)',
    fontWeight: 700,
  },
  statsSize: {
    whiteSpace: 'nowrap',
    color: 'var(--text-muted)',
    fontWeight: 500,
  },
  statsSep: {
    width: '1px',
    height: '18px',
    background: 'var(--border)',
  },
  statsCount: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    color: 'var(--text)',
    fontWeight: 500,
  },
  statsCountNum: {
    color: '#ef4444',
    fontWeight: 700,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: '20px',
    marginBottom: '24px',
  },
  card: (isRemoving) => ({
    background: 'var(--surface)',
    border: isRemoving ? '2px solid #ef4444' : '1px solid var(--border)',
    borderRadius: '12px',
    padding: '12px',
    position: 'relative',
    transition: 'transform 0.15s, border-color 0.15s, box-shadow 0.15s',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minHeight: '240px',
    cursor: 'pointer',
    boxShadow: isRemoving ? '0 8px 20px rgba(239, 68, 68, 0.12)' : 'none',
    opacity: isRemoving ? 0.7 : 1,
  }),
  thumbnail: {
    width: '100%',
    aspectRatio: '1 / 1.41',
    background: 'var(--surface2)',
    borderRadius: '6px',
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    border: '1px solid var(--border)',
    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  thumbImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  pageLabel: {
    fontSize: '12px',
    color: 'var(--text)',
    textAlign: 'center',
    fontWeight: 600,
  },
  actions: {
    position: 'absolute',
    top: '12px',
    right: '12px',
    display: 'flex',
    gap: '6px',
    zIndex: 10,
  },
  actionBtn: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    background: 'var(--surface)',
    border: '1px solid var(--border-md)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: 'var(--text-muted)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    transition: 'background 0.2s, color 0.2s',
  },
  removeBadge: (isRemoving) => ({
    position: 'absolute',
    top: '12px',
    left: '12px',
    zIndex: 10,
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    background: isRemoving ? '#ef4444' : 'var(--surface)',
    border: isRemoving ? 'none' : '2px solid var(--border-hi)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    transition: 'background 0.2s, border-color 0.2s',
  }),
  overlayIcon: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    color: '#ef4444',
    zIndex: 5,
    opacity: 0.8,
  }
};

const Thumbnail = ({ file, pageIndex, rotation }) => {
  const [thumb, setThumb] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    let active = true;
    const fetchThumb = async () => {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('page', pageIndex);
        
        const res = await fetch(`${API_BASE}/render-pdf`, {
          method: 'POST',
          body: formData,
        });
        
        if (res.ok && active) {
          const blob = await res.blob();
          setThumb(URL.createObjectURL(blob));
        }
      } catch (e) {
        console.error("Thumb failed", e);
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchThumb();
    return () => { active = false; if (thumb) URL.revokeObjectURL(thumb); };
  }, [file, pageIndex, isVisible]);

  return (
    <div ref={containerRef} style={{ ...styles.thumbnail, transform: `rotate(${rotation}deg)` }}>
      {loading ? (
        <RotateCw className="animate-spin" size={24} color="var(--text-muted)" />
      ) : thumb ? (
        <img src={thumb} alt={`Page ${pageIndex + 1}`} style={styles.thumbImg} />
      ) : (
        <FileText size={48} color="var(--border-hi)" />
      )}
    </div>
  );
};

const PageCard = ({ file, pageIndex, rotation, isRemoving, onToggle, onRotate }) => {
  return (
    <div 
      style={styles.card(isRemoving)}
      onClick={onToggle}
    >
      <div style={styles.removeBadge(isRemoving)}>
        {isRemoving && <X size={14} strokeWidth={4} />}
      </div>
      
      {isRemoving && (
        <div style={styles.overlayIcon}>
            <Trash2 size={48} strokeWidth={1.5} />
        </div>
      )}

      <div style={styles.actions}>
        <div 
          style={styles.actionBtn} 
          onClick={(e) => { e.stopPropagation(); onRotate(); }}
          title="Rotate 90°"
        >
          <RotateCw size={14} />
        </div>
      </div>
      <Thumbnail file={file} pageIndex={pageIndex} rotation={rotation} />
      <div style={styles.pageLabel}>Page {pageIndex + 1}</div>
    </div>
  );
};

export default function PDFRemovePages({ file, rotations, onRotatePage, selectedPages, setSelectedPages }) {
  const [pageCount, setPageCount] = useState(0);

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await fetch(`${API_BASE}/convert/inspect`, {
          method: 'POST',
          body: formData,
        });
        if (res.ok) {
          const data = await res.json();
          setPageCount(data.totalPages);
          // For removal, we start with NOTHING selected (meaning we keep all pages by default)
          if (selectedPages.length === 0) {
            // No action needed, selectedPages is already empty or from previous state
          }
        }
      } catch (e) {}
    };
    fetchInfo();
  }, [file]);

  const togglePage = (idx) => {
    setSelectedPages(prev => 
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx].sort((a,b) => a-b)
    );
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.stats}>
          <div style={styles.statsIcon}>
            <Trash2 size={18} strokeWidth={2.5} />
          </div>
          <div style={styles.statsText}>
            <span style={styles.statsSize}>{(file.size / (1024*1024)).toFixed(2)} MB</span>
            <div style={styles.statsSep} />
            <div style={styles.statsCount}>
              <span style={styles.statsCountNum}>{selectedPages.length}</span>
              <span>/ {pageCount} Pages to Remove</span>
            </div>
          </div>
        </div>
      </div>

      <div style={styles.grid}>
        {Array.from({ length: pageCount }).map((_, idx) => (
          <PageCard
            key={`page-${idx}`}
            file={file}
            pageIndex={idx}
            rotation={rotations[idx] || 0}
            isRemoving={selectedPages.includes(idx)}
            onToggle={() => togglePage(idx)}
            onRotate={() => onRotatePage(idx)}
          />
        ))}
      </div>
    </div>
  );
}
